import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { terminalsService } from '../../../services/terminalsService';
import { LookupDto } from '../../../models/common/lookup.dto';
import { TerminalFilter } from '../../../models/terminals/terminalFilter';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

interface TerminalsAutocompleteFormControlProps {
    name: string;
    companyId?: number | null;
    label?: string;
    style?: React.CSSProperties;
    required?: boolean;
}

const TerminalsAutocompleteFormControl: React.FC<TerminalsAutocompleteFormControlProps> = ({ name, companyId, label = 'Терминалы', style, required }) => {
    const { control, setValue } = useFormContext();
    const [options, setOptions] = useState<LookupDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const debouncedSearchText = useDebouncedValue(inputValue, 400);
    const companyChangedRef = useRef(false);

    useEffect(() => {
        setInputValue('');
        setValue(name, []);
        companyChangedRef.current = true;
    }, [companyId, name, setValue]);

    useEffect(() => {
        if (!companyId) {
            setOptions([]);
            return;
        }

        let cancelled = false;
        setLoading(true);

        const nameFilter = companyChangedRef.current ? undefined : (debouncedSearchText || undefined);
        companyChangedRef.current = false;

        const filter: TerminalFilter = {
            companyId,
            page: 0,
            pageSize: 50,
            name: nameFilter,
        };

        terminalsService.getByFilter(filter)
            .then(results => {
                if (!cancelled) {
                    setOptions(results.data.data.items);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setOptions([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [companyId, debouncedSearchText]);

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: required ? 'Обязательное поле' : false }}
            render={({ field, fieldState }) => (
                <Autocomplete
                    multiple
                    options={options}
                    getOptionLabel={option => option.name}
                    getOptionKey={option => option.id}
                    filterOptions={x => x}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={field.value || []}
                    inputValue={inputValue}
                    onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                    onChange={(_, value) => field.onChange(value)}
                    loading={loading}
                    noOptionsText="Не найдено"
                    renderInput={params => (
                        <TextField
                            {...params}
                            label={label}
                            placeholder="Введите название или адрес"
                            variant="outlined"
                            margin="dense"
                            size="small"
                            style={style}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            required={required}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    size="small"
                    disabled={!companyId}
                />
            )}
        />
    );
};

export default TerminalsAutocompleteFormControl;
